import { ChevronRight } from 'lucide-react'
import { Link } from 'react-router'
import { cn } from '@/lib/utils'
import type { DayListRow } from './day-list'

const ROW_FOCUS = 'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint'

/**
 * One row of the day list or the phone agenda: an icon tile, what it is in
 * words, and a chevron when it opens something. At least 44px tall, the touch
 * target a thumb needs.
 *
 * A row with no `onOpen` (a block, which has no sheet of its own) is shown
 * the same way but is not a button, so it never promises a tap.
 */
export function DayListRowButton({ row }: { row: DayListRow }) {
  const body = (
    <>
      <span
        aria-hidden="true"
        className={cn(
          'flex h-7 w-7 shrink-0 items-center justify-center rounded border',
          row.tileClassName
        )}
      >
        {row.icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block break-words text-sm font-medium text-text">{row.title}</span>
        {row.detail && (
          <span className="block break-words text-xs text-textMuted">{row.detail}</span>
        )}
      </span>
    </>
  )
  const layout = 'flex min-h-11 w-full items-center gap-3 rounded-md border border-border px-3 py-2'
  const main = row.onOpen ? (
    <button
      type="button"
      data-testid={row.testId}
      {...row.data}
      aria-label={row.ariaLabel}
      onClick={row.onOpen}
      className={cn(layout, 'text-left hover:bg-surface2', ROW_FOCUS)}
    >
      {body}
      <ChevronRight className="h-4 w-4 shrink-0 text-textMuted" aria-hidden="true" />
    </button>
  ) : (
    <div data-testid={row.testId} {...row.data} className={layout}>
      {body}
    </div>
  )
  if (!row.link) return main
  return (
    <div className="space-y-0.5">
      {main}
      <Link
        to={row.link.to}
        data-testid={row.link.testId}
        className={cn(
          'ml-10 inline-flex min-h-11 items-center rounded text-xs text-mint underline-offset-2 hover:underline',
          ROW_FOCUS
        )}
      >
        {row.link.label}
      </Link>
    </div>
  )
}
